import type { CSSProperties } from 'react';

/** Optional document-root bottom clearance (e.g. staging footer). Sidebar modals sit above it. */
export const APP_BOTTOM_CLEARANCE_CSS_VAR = '--app-bottom-clearance';

/**
 * Published each animation frame from the right sidebar panel's on-screen inset.
 * Followers use this with no CSS transition — they track the panel's real motion.
 */
export const RIGHT_SIDEBAR_FOLLOWER_TRANSLATE_CSS_VAR = '--right-sidebar-follower-translate';

/** How far a right sidebar panel currently covers from the viewport's right edge (px). */
export const getRightSidebarVisibleInsetPx = (panel: HTMLElement, viewportWidth = window.innerWidth): number => {
    const { left } = panel.getBoundingClientRect();
    return Math.max(0, viewportWidth - left);
};

/**
 * Style for fixed overlays that should clear an open right sidebar.
 * No transition: the translate CSS var is updated every frame from the panel rect.
 */
export const getRightSidebarFollowerStyle = (): CSSProperties => ({
    transform: `translateX(var(${RIGHT_SIDEBAR_FOLLOWER_TRANSLATE_CSS_VAR}, 0px))`,
});

const trackedRightSidebarPanels = new Map<symbol, HTMLElement>();
let followerRafId = 0;

const syncFollowerTranslateCssVar = () => {
    const rootStyle = document.documentElement.style;
    if (trackedRightSidebarPanels.size === 0) {
        rootStyle.removeProperty(RIGHT_SIDEBAR_FOLLOWER_TRANSLATE_CSS_VAR);
        return;
    }

    const maxInsetPx = [...trackedRightSidebarPanels.values()].reduce(
        (maxInset, panel) => Math.max(maxInset, getRightSidebarVisibleInsetPx(panel)),
        0,
    );
    rootStyle.setProperty(RIGHT_SIDEBAR_FOLLOWER_TRANSLATE_CSS_VAR, `${-maxInsetPx}px`);
};

const stopFollowerLoop = () => {
    if (followerRafId !== 0) {
        cancelAnimationFrame(followerRafId);
        followerRafId = 0;
    }
};

const tickFollowerLoop = () => {
    syncFollowerTranslateCssVar();
    if (trackedRightSidebarPanels.size === 0) {
        followerRafId = 0;
        return;
    }
    followerRafId = requestAnimationFrame(tickFollowerLoop);
};

const startFollowerLoop = () => {
    if (followerRafId !== 0) {
        return;
    }
    followerRafId = requestAnimationFrame(tickFollowerLoop);
};

/**
 * Samples a right sidebar panel's layout each frame and publishes
 * {@link RIGHT_SIDEBAR_FOLLOWER_TRANSLATE_CSS_VAR} so overlays stay locked to the slide.
 * @returns Cleanup — call when the panel unmounts (including after leave).
 */
export const registerRightSidebarFollowerPanel = (panel: HTMLElement): (() => void) => {
    const key = Symbol('right-sidebar-follower-panel');
    trackedRightSidebarPanels.set(key, panel);
    // Measure on the next frame. A sync getBoundingClientRect here runs in the
    // panel ref during commit and delays Headless UI's enter slide.
    startFollowerLoop();

    return () => {
        trackedRightSidebarPanels.delete(key);
        if (trackedRightSidebarPanels.size === 0) {
            stopFollowerLoop();
            syncFollowerTranslateCssVar();
            return;
        }
        syncFollowerTranslateCssVar();
    };
};

const openModalKeys = new Set<symbol>();
const openModalListeners = new Set<() => void>();

/** Set on `document.documentElement` while a modal has finished opening. Overlays can key CSS off it. */
export const OPEN_MODAL_DOCUMENT_ATTR = 'data-cpool-modal-open';

const syncOpenModalDocumentAttr = () => {
    if (typeof document === 'undefined') {
        return;
    }
    if (openModalKeys.size > 0) {
        document.documentElement.setAttribute(OPEN_MODAL_DOCUMENT_ATTR, '');
        return;
    }
    document.documentElement.removeAttribute(OPEN_MODAL_DOCUMENT_ATTR);
};

const notifyOpenModalListeners = () => {
    syncOpenModalDocumentAttr();
    openModalListeners.forEach((listener) => listener());
};

/** True while at least one modal reports itself as fully open. */
export const getHasOpenModal = (): boolean => openModalKeys.size > 0;

/**
 * Marks one modal as fully open so overlays outside the dialog can react to it.
 * Also publishes {@link OPEN_MODAL_DOCUMENT_ATTR} so overlays can show without a React re-render.
 * @returns Cleanup — call when the modal closes or unmounts.
 */
export const registerOpenModal = (): (() => void) => {
    const key = Symbol('open-modal');
    openModalKeys.add(key);
    notifyOpenModalListeners();

    return () => {
        if (!openModalKeys.delete(key)) {
            return;
        }
        notifyOpenModalListeners();
    };
};

/**
 * Subscribes to changes of the open modal registry.
 * @returns Cleanup — call to remove the listener.
 */
export const subscribeToOpenModals = (listener: () => void): (() => void) => {
    openModalListeners.add(listener);

    return () => {
        openModalListeners.delete(listener);
    };
};

/**
 * Timing of the sidebar panel slide, mirroring the classes {@link getSlideTransition} returns.
 * Followers reuse it so they land with the panel instead of ahead of it.
 */
export const SIDEBAR_SLIDE_TIMING = {
    /** Tailwind's `ease-out`. */
    enterEasing: 'cubic-bezier(0, 0, 0.2, 1)',
    /** Tailwind's `duration-300`. */
    enterMs: 300,
    /** Tailwind's `ease-in`. */
    leaveEasing: 'cubic-bezier(0.4, 0, 1, 1)',
    /** Tailwind's `duration-200`. */
    leaveMs: 200,
} as const;

/**
 * Returns transition classes for sidebar modal slide animation.
 * @param side - The side from which the sidebar should slide in ('left' or 'right')
 * @returns Transition classes object for Headless UI Transition component
 */
export const getSlideTransition = (side: 'left' | 'right') => ({
    enter: 'transition ease-out duration-300 transform',
    enterFrom: side === 'left' ? '-translate-x-full' : 'translate-x-full',
    enterTo: 'translate-x-0',
    leave: 'transition ease-in duration-200 transform',
    leaveFrom: 'translate-x-0',
    leaveTo: side === 'left' ? '-translate-x-full' : 'translate-x-full',
});

/**
 * Returns transition classes for default modal fade and scale animation.
 * @returns Transition classes object for Headless UI Transition component
 */
export const getDefaultTransition = () => ({
    enter: 'transition ease-out duration-150 transform',
    enterFrom: 'opacity-0 scale-95',
    enterTo: 'opacity-100 scale-100',
    leave: 'transition ease-out duration-150',
    leaveFrom: 'opacity-100 scale-100',
    leaveTo: 'opacity-0 scale-95',
});
